/**
 * PMI Settings Proxy
 * Creates a proxy around window.globalSettings to intercept property access
 * and log warnings when undefined properties are accessed.
 */

import { globalSettingsProps } from '../../../interfaces/globalSettings';
import { isDeveloperMode } from '../../utils';

export {};

declare global {
  interface Window {
    globalSettings: globalSettingsProps;
    createProxy?: typeof createProxy;
  }
}

function createProxy<T extends object>(obj: T, path: string[] = []): T {
  return new Proxy(obj, {
    get(target: T, prop: string | symbol) {
      const fullPath = [...path, String(prop)].join('.');
      const value = (target as any)[prop];

      if (value === undefined) {
        console.groupCollapsed(`🚨 Undefined Property Access: window.globalSettings.${fullPath}`);
        console.warn('Full stack trace:');
        console.trace();
        console.groupEnd();
        return undefined;
      }

      // If value is an object, wrap it again
      if (value && typeof value === 'object') {
        return createProxy(value, [...path, String(prop)]);
      }

      return value;
    },
  }) as T;
}

// Monitor for globalSettings to become defined and apply proxy once
export function startMonitoring(): void {
  if (typeof window === 'undefined' || !isDeveloperMode()) {
    return;
  }

  // Check every 100ms if globalSettings becomes defined
  const checkInterval = setInterval(() => {
    if (window.globalSettings !== undefined) {
      window.globalSettings = createProxy(window.globalSettings);
      // Clear the interval immediately after applying proxy once
      clearInterval(checkInterval);
    }
  }, 100);

  // Stop monitoring after 10 seconds (settings.js should load by then)
  setTimeout(() => {
    clearInterval(checkInterval);
    console.log('PMI Settings Proxy monitoring timeout');
  }, 10000);
}

// Make createProxy available globally (optional, for debugging)
if (typeof window !== 'undefined') {
  window.createProxy = createProxy;
}

startMonitoring();
